import get from 'lodash/get';

import { PROFILE_FIELDS } from '../../../constants/strings';
import { SPACE } from '../../../constants/chars';
import { RULES } from '../../../utils/validations';
import generateMrnDescriptors from './generateMrnDescriptors';
import generateEmployersDescriptors from './generateEmployersDescriptors';
import generatePharmacyDescription from './generatePharmacyDescription';

const getPrimaryPracticeAddressFields = (options, formatMessage) => {
  const {
    practiceAddress1,
    practiceAddress2,
    practiceCity,
    practiceState,
    practiceZip,
    practiceCountry,
  } = options;
  return [
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeAddress1),
      value: practiceAddress1,
      fieldName: 'practiceAddress1',
      maxLength: 50,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeAddress2),
      value: practiceAddress2,
      fieldName: 'practiceAddress2',
      maxLength: 50,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeCity),
      value: practiceCity,
      fieldName: 'practiceCity',
      maxLength: 50,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeState),
      value: practiceState,
      fieldName: 'practiceState',
      separator: SPACE,
      maxLength: 20,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.postalCode),
      value: practiceZip,
      fieldName: 'practiceZip',
      maxLength: 20,
    },
    {
      name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeCountry),
      value: practiceCountry,
      fieldName: 'practiceCountry',
      maxLength: 50,
      disabled: true,
    },
  ];
};

export const getProviderInfoFieldsetList = ({
  canEdit,
  profile,
  intl: { formatMessage },
}) => {
  const { primaryProviderProfile } = profile;
  const { npi, officePhone, localUid, role } = primaryProviderProfile || {};

  const primaryPracticeAddressFields = getPrimaryPracticeAddressFields(
    primaryProviderProfile,
    formatMessage,
  );

  let primaryPracticeAddressFieldsList = primaryPracticeAddressFields;
  if (!canEdit) {
    primaryPracticeAddressFieldsList = [
      {
        name: formatMessage(PROFILE_FIELDS.fieldNames.primaryPracticeAddress),
        separator: ', ',
        value: primaryPracticeAddressFields,
      },
    ];
  }

  return [
    {
      title: formatMessage(PROFILE_FIELDS.listTitles.prof),
      fields: [
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.localUidProvider),
          value: localUid,
          disabled: true,
          mode: 'view',
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.npi),
          value: npi,
          disabled: true,
          mode: 'view',
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.role),
          value: role,
          fieldName: 'role',
          maxLength: 50,
        },
        ...primaryPracticeAddressFieldsList,
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.officePhone),
          value: officePhone,
          fieldName: 'officePhone',
          validationRules: {
            [RULES.PHONE]: {
              message: 'profile.phone.format',
            },
          },
          maxLength: 50,
        },
      ],
      columns: true,
    },
  ];
};
// TODO too many transformations
export function formatExternalList(list) {
  if (!list) return list;
  const filteredList = list.filter(item => item.value.enabled);
  return filteredList.map(item => ({
    ...item,
    value: formatItem(item.value),
  }));
}
// TODO too many transformations
function formatInternalList(list) {
  if (!list) return list;

  return list.map(formatItem);
}

function formatItem(item) {
  return {
    ...item,
    value: item.name,
  };
}

export const getPatientInfoFieldsetList = ({
  profile,
  location,
  medicationAllergiesList,
  healthConditionsList,
  intl: { formatMessage },
}) => {
  const {
    primaryPatientProfile,
    actualFamilyMember,
    employeeProfile,
  } = profile;
  const {
    medicationAllergies,
    healthConditions,
    healthGoals,
    mrns,
    patientProfileNdcDrugs,
    pharmacy,
  } = primaryPatientProfile || {};
  const isFamilyMember = location.pathname.includes('family-member');
  const list = [];
  const getSafeMedicineNames = patientProfileNdcDrugs.map(drug => ({
    id: drug.ndcDrugId,
    name: drug.displayableName,
    value: drug.displayableName,
  }));

  if (!isFamilyMember && !actualFamilyMember) {
    list.push({
      title: formatMessage(PROFILE_FIELDS.listTitles.health),
      columns: true,
      fields: [
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.healthConditions),
          value: formatInternalList(healthConditions),
          multi: true,
          type: 'select',
          fieldName: 'healthConditionIds',
          options: formatExternalList(healthConditionsList),
          onCheckBeforeDelete: checkedValue => {
            const id = get(checkedValue, 'value.id');
            const condition = healthConditionsList.find(
              item => item.value.id === id,
            );
            return !condition;
          },
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.medicationAllergies),
          value: formatInternalList(medicationAllergies),
          multi: true,
          type: 'select',
          fieldName: 'medicationAllergyIds',
          options: formatExternalList(medicationAllergiesList),
          onCheckBeforeDelete: checkedValue => {
            const id = get(checkedValue, 'value.id');
            const allergy = medicationAllergiesList.find(
              item => item.value.id === id,
            );
            return !allergy;
          },
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.medicine),
          value: getSafeMedicineNames,
          fieldName: 'ndcDrugIds',
          type: 'medication',
        },
        {
          name: formatMessage(PROFILE_FIELDS.fieldNames.healthGoals),
          value: healthGoals,
          fieldName: 'healthGoals',
        },
      ],
    });

    list.push({
      title: formatMessage(PROFILE_FIELDS.fieldNames.preferredPharmacy),
      columns: false,
      fields: generatePharmacyDescription(pharmacy, formatMessage),
      disabled: false,
    });

    if (mrns && mrns.length) {
      list.push({
        title: formatMessage(PROFILE_FIELDS.fieldNames.organizations),
        columns: true,
        fields: generateMrnDescriptors(mrns),
        disabled: true,
      });
    }

    if (employeeProfile && employeeProfile.employeeEmployers.length) {
      list.push({
        title: formatMessage(PROFILE_FIELDS.listTitles.affiliations),
        columns: true,
        fields: generateEmployersDescriptors(employeeProfile, formatMessage),
        useCustomAction: true,
      });
    }
  }

  return list;
};



// WEBPACK FOOTER //
// ./src/components/ProfileLayout/utils/getInfoFieldset.js